import { useCallback, useEffect, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from 'react'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { useEdgeFadeScroll } from '../../hooks/useEdgeFadeScroll'
import { getNlsString } from '../../nls/nlsRuntime'
import { HeaderInitialChip } from './HeaderInitialChip'
import {
  RAIL_MIN_2C,
  RAIL_MIN_3C,
  SIDECAR_MIN,
  clampSide,
  keyboardDelta,
  readWidths,
  shouldSnapCollapse,
  shouldSnapExpand,
  writeWidths,
  type ShellWidths,
} from './railPageShellResize'
import { useIsMobile, useIsPhone } from './useIsMobile'
import { Pressable } from './Pressable'
import './RailPageShell.css'

/**
 * Shell-scoped collapse state. Rail-item components (e.g. `RailChipTabs`)
 * read this to switch to icon-only rendering. `null` means "no collapsible
 * shell above me" — components should behave as if expanded.
 */
import {
  RailPageShellCollapseContext,
  type RailPageShellCollapseState,
} from './railPageShellContext'

export type { RailPageShellCollapseState }

const COLLAPSE_STORAGE_PREFIX = 'railPageShell:collapsed:'

const readCollapsed = (key: string | null): boolean => {
  if (!key) return false
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_PREFIX + key) === '1'
  } catch {
    return false
  }
}

const writeCollapsed = (key: string | null, value: boolean): void => {
  if (!key) return
  try {
    window.localStorage.setItem(COLLAPSE_STORAGE_PREFIX + key, value ? '1' : '0')
  } catch {
    /* private-window / disabled storage — collapse is session-only */
  }
}

export type RailPageShellVariant = 'three-column' | 'two-column' | 'one-column' | 'full'

export type RailPageShellIdentityProps = {
  badge?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  className?: string
  /**
   * `title-only` renders the identity as a larger, single-line card (Focus-
   * style). `standard` renders badge + title + subtitle (Meetings-style).
   */
  variant?: 'standard' | 'title-only'
}

export type RailPageShellSectionHeaderProps = {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  className?: string
}

/**
 * Image-4-style card frame. Everything is optional — a card can either pass
 * `eyebrow` / `heading` / `subtitle` / `footer` props to render the standard
 * chrome, or drop freeform children in and use its own internal layout while
 * still inheriting the card border/background/shadow.
 */
export type RailPageShellCardProps = HTMLAttributes<HTMLElement> & {
  as?: 'section' | 'article' | 'aside' | 'div'
  children?: ReactNode
  eyebrow?: ReactNode
  heading?: ReactNode
  subtitle?: ReactNode
  footer?: ReactNode
}

type CommonRailPageShellProps = {
  variant: RailPageShellVariant
  /**
   * @deprecated Aurora (twinkling particle background) was removed
   * 2026-08-23 for perf reasons. This prop is accepted for source-level
   * backward compatibility but has no effect. Safe to delete from callers.
   */
  aurora?: boolean
  className?: string
  ariaLabel?: string
  /** @deprecated See `aurora`. Kept as a no-op for source compatibility. */
  auroraParticleCount?: number
  /**
   * When `false`, the shell renders its layout contract (grid, height,
   * variant column tracks) WITHOUT the outer background / border / rounded
   * chrome. Use for pages that already own a bespoke outer surface — Design
   * Hub (`.dh-shell`), Raid, Checklists (`.clShell`), Link Hub — so we
   * don't double-paint a background around them.
   * Default: `true` (shell renders full chrome).
   */
  chrome?: boolean
}

export type RailPageShellColumnSizes = {
  /** Width of the left rail column (2-col + 3-col). Default: 320-380px (2-col) / 220px (3-col). */
  left?: string
  /** Width of the right sidecar column (3-col only). Default: 280px. */
  right?: string
}

export type RailPageShellProps = CommonRailPageShellProps & {
  /** Identity card (2-col + 3-col only). Rendered as a flat header row inside the rail card. */
  identity?: RailPageShellIdentityProps

  /**
   * Left rail body content (2-col + 3-col). Rendered inside a scroll region.
   * Prefer the split-slot API below (`leftRailList` + `leftRailFilter` +
   * `leftRailBottom`) for pages that want distinct sub-sections without
   * nested card borders. When `leftRail` is passed, it renders as-is and the
   * split slots are ignored.
   */
  leftRail?: ReactNode

  /**
   * Split left-rail slots — stacked vertically inside the same rail card
   * with no nested borders:
   *   `leftRailList`   — LCM: chips/tabs ONLY (typically `RailChipTabs`).
   *   `leftRailFilter` — optional filter controls that sit between LCM & LCB.
   *   `leftRailExtras` — legacy slot; still rendered inside the scroll region
   *                      for pages not yet migrated. New code should prefer
   *                      `leftRailList` (LCM) or `leftRailBottom` (LCB).
   *   `leftRailBottom` — LCB: flat sticky-bottom region with a top HR. Houses
   *                      indicators, status footers, or filter footers.
   * Ignored when `leftRail` is provided (backward compat).
   */
  leftRailList?: ReactNode
  leftRailFilter?: ReactNode
  leftRailExtras?: ReactNode
  leftRailBottom?: ReactNode

  /**
   * Icon shown centered in the LCB region when the rail is collapsed. If
   * omitted, `leftRailBottom` is hidden entirely in the collapsed view.
   * Only meaningful when `leftRailBottom` is also passed and the shell is
   * `collapsible`.
   */
  leftRailBottomIcon?: ReactNode

  /**
   * Enables the rail-collapse affordance (2-col + 3-col only). When true,
   * the identity badge morphs into a chevron on hover; clicking toggles the
   * rail between full-width and a narrow icon-only column. Defaults to
   * `true` for 2c/3c pages that pass an `identity`, `false` otherwise.
   */
  collapsible?: boolean

  /**
   * localStorage key suffix used to persist the collapse state per page.
   * Falls back to the identity title (string) or the `ariaLabel`. Pass an
   * explicit key when neither is a stable string.
   */
  storageKey?: string

  /**
   * When true, rail-item components inside this shell should render their
   * collapsed state as an initials avatar (first 1-2 letters of the label)
   * instead of an icon. Used for the two homogeneous-list exempt surfaces —
   * Chat channel/tag rail and AI Magic history session rows.
   */
  useCollapsedInitials?: boolean

  /**
   * How the rail body should render in collapsed state:
   *   `iconify` (default) — LC drops to a uniform 64px, each row becomes
   *     an icon-only chip.
   *   `hide` — the whole rail body disappears in collapsed mode; only the
   *     identity chevron (and optional `leftRailBottomIcon`) remains. Use
   *     for pages whose rail body is a form or stat-card stack that doesn't
   *     map to icons (Focus, Raid, Todos).
   */
  collapsedRailBehavior?: 'iconify' | 'hide'

  /** Middle stage — 3-col only. */
  middleHeader?: RailPageShellSectionHeaderProps
  middleStage?: ReactNode

  /** Right sidecar — 3-col only. */
  rightSidecar?: ReactNode

  /** Right stage — 2-col only. */
  rightStage?: ReactNode

  /** 1-column layout. */
  header?: ReactNode
  body?: ReactNode

  /** Full-variant free children (also used as a fallback). */
  children?: ReactNode

  /** Per-page column-size overrides. */
  columnSizes?: RailPageShellColumnSizes

  /** Whether the left rail / right sidecar columns are user-resizable. Default true for rail variants. */
  resizable?: boolean

  /** Phone layout strategy. 'shell' (default) gives 2c/3c pages a single-column layout with horizontal top rail and bottom sheet sidecar. 'page' preserves legacy layout. */
  phoneLayout?: 'shell' | 'page'

  /** Phone rail bottom handling. 'show' (default) renders leftRailBottom after stage content on phones. 'hide' suppresses decorative rail footers. */
  phoneRailBottom?: 'show' | 'hide'

  /** Optional controlled open state for the phone sidecar bottom sheet. */
  sidecarOpen?: boolean

  /** Callback fired when the phone sidecar open state changes. */
  onSidecarOpenChange?: (open: boolean) => void
}

const joinClasses = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ')

/**
 * Identity card — top of every left rail.
 *
 * - `standard` — badge (optional) + title + subtitle. Meetings/Forms style.
 * - `title-only` — larger title, no badge/subtitle. Focus/Chat style.
 */
export const RailPageShellIdentity = ({
  badge,
  title,
  subtitle,
  className,
  variant = 'standard',
  collapsed = false,
  onToggleCollapsed,
}: RailPageShellIdentityProps & {
  collapsed?: boolean
  onToggleCollapsed?: () => void
}) => {
  const layoutClass = variant === 'title-only'
    ? 'railPageShellIdentity railPageShellIdentity--title-only'
    : 'railPageShellIdentity'
  const canToggle = typeof onToggleCollapsed === 'function'
  const badgeContent = badge !== undefined ? (
    <HeaderInitialChip>{badge}</HeaderInitialChip>
  ) : null
  const chevronContent = (
    <span className="railPageShellIdentity__chevron" aria-hidden="true">
      {collapsed
        ? <ChevronRightRoundedIcon fontSize="small" />
        : <ChevronLeftRoundedIcon fontSize="small" />}
    </span>
  )
  const renderBadgeSlot = () => {
    if (!canToggle) return badgeContent
    return (
      <Pressable
        className={joinClasses(
          'railPageShellIdentity__toggle',
          collapsed && 'railPageShellIdentity__toggle--collapsed',
          badge === undefined && 'railPageShellIdentity__toggle--noBadge',
        )}
        onClick={onToggleCollapsed}
        aria-label={collapsed ? 'Expand rail' : 'Collapse rail'}
        aria-expanded={!collapsed}
      >
        {badgeContent !== null && (
          <span className="railPageShellIdentity__toggle__badge">{badgeContent}</span>
        )}
        <span className="railPageShellIdentity__toggle__chevron">{chevronContent}</span>
      </Pressable>
    )
  }
  return (
    <header className={joinClasses(layoutClass, className)}>
      {variant === 'standard' && (badgeContent !== null || canToggle) && renderBadgeSlot()}
      <div className="railPageShellIdentity__meta">
        {variant === 'standard' && subtitle !== undefined && (
          <span className="railPageShellIdentity__subtitle">{subtitle}</span>
        )}
        <strong className="railPageShellIdentity__title">{title}</strong>
        {variant === 'title-only' && subtitle !== undefined && (
          <span className="railPageShellIdentity__subtitle">{subtitle}</span>
        )}
      </div>
    </header>
  )
}

/**
 * Switching section header — used as the top card of the middle stage on
 * 3-col pages. Renders FLAT (no card border/background) per the UI-MP3CMTB
 * contract: LS = title+subtitle on the left, RS = actions on the right.
 */
export const RailPageShellSectionHeader = ({
  title,
  subtitle,
  actions,
  className,
}: RailPageShellSectionHeaderProps) => (
  <section className={joinClasses('railPageShellSectionHeader', className)}>
    <div className="railPageShellSectionHeader__meta">
      <h1 className="railPageShellSectionHeader__title">{title}</h1>
      {subtitle !== undefined && (
        <p className="railPageShellSectionHeader__subtitle">{subtitle}</p>
      )}
    </div>
    {actions !== undefined && (
      <div className="railPageShellSectionHeader__actions">{actions}</div>
    )}
  </section>
)

/**
 * Reusable card wrapper matching the shell's Design-Hub-derived card tokens.
 * When `eyebrow` / `heading` / `subtitle` / `footer` props are passed, the
 * card renders the image-4 chrome (eyebrow · title · subtitle · body · footer
 * CTA row). Freeform children are placed in the body region.
 */
export const RailPageShellCard = ({
  as = 'section',
  className,
  children,
  eyebrow,
  heading,
  subtitle,
  footer,
  ...rest
}: RailPageShellCardProps) => {
  const Tag = as as 'section'
  const hasStructuredHead = eyebrow !== undefined || heading !== undefined || subtitle !== undefined
  const hasFooter = footer !== undefined
  const structured = hasStructuredHead || hasFooter
  return (
    <Tag
      className={joinClasses(
        'railPageShell__card',
        structured && 'railPageShell__card--image4',
        className,
      )}
      {...rest}
    >
      {hasStructuredHead && (
        <header className="railPageShell__card__head">
          {eyebrow !== undefined && (
            <span className="railPageShell__card__eyebrow">{eyebrow}</span>
          )}
          {heading !== undefined && (
            <strong className="railPageShell__card__heading">{heading}</strong>
          )}
          {subtitle !== undefined && (
            <p className="railPageShell__card__subtitle">{subtitle}</p>
          )}
        </header>
      )}
      {children !== undefined && (
        structured
          ? <div className="railPageShell__card__body">{children}</div>
          : children
      )}
      {hasFooter && (
        <footer className="railPageShell__card__footer">{footer}</footer>
      )}
    </Tag>
  )
}

const IdentityBlock = ({
  identity,
  collapsed,
  onToggleCollapsed,
}: {
  identity: RailPageShellIdentityProps
  collapsed?: boolean
  onToggleCollapsed?: () => void
}) => (
  <RailPageShellIdentity
    {...identity}
    collapsed={collapsed}
    onToggleCollapsed={onToggleCollapsed}
  />
)

/**
 * RailPageShell — the shared shell for every right-rail page.
 *
 * Renders one of four layout variants (`three-column`, `two-column`,
 * `one-column`, `full`), applies the shared aurora background, and clamps to
 * the viewport height using the same collapsing-top-bar contract Design Hub
 * uses (see design.css:42-75). Pages compose their content into the named
 * slots; every child card should use `RailPageShellCard` or the
 * `railPageShell__card` class to inherit the shell's card tokens.
 *
 * The shell is presentational only — it owns no state and no side effects.
 * All keyboard handlers, drawers, dialogs, and page-level overlays should
 * render OUTSIDE the shell as they do today.
 */
export const RailPageShell = ({
  variant,
  className,
  ariaLabel,
  chrome = true,
  identity,
  leftRail,
  leftRailList,
  leftRailFilter,
  leftRailExtras,
  leftRailBottom,
  leftRailBottomIcon,
  collapsible,
  storageKey,
  useCollapsedInitials = false,
  collapsedRailBehavior = 'iconify',
  middleHeader,
  middleStage,
  rightSidecar,
  rightStage,
  header,
  body,
  children,
  columnSizes,
  resizable,
  phoneLayout = 'shell',
  phoneRailBottom = 'show',
  sidecarOpen: sidecarOpenProp,
  onSidecarOpenChange,
}: RailPageShellProps) => {
  const isRailVariant = variant === 'three-column' || variant === 'two-column'
  const isMobile = useIsMobile()
  const isPhone = useIsPhone()
  const isPhoneShellActive = phoneLayout === 'shell' && isPhone
  const resizeEnabled = isRailVariant && (resizable ?? true) && !isMobile && !isPhoneShellActive
  const collapseEnabled = isRailVariant
    && (collapsible ?? identity !== undefined)
    && !isPhoneShellActive

  const identityTitle = typeof identity?.title === 'string' ? identity.title : null
  const derivedStorageKey = useMemo(() => {
    if (!collapseEnabled) return null
    if (storageKey) return storageKey
    return identityTitle ?? ariaLabel ?? null
  }, [collapseEnabled, storageKey, identityTitle, ariaLabel])

  const derivedWidthKey = useMemo(() => {
    if (!isRailVariant) return null
    if (storageKey) return storageKey
    return identityTitle ?? ariaLabel ?? null
  }, [isRailVariant, storageKey, identityTitle, ariaLabel])

  const [prevCollapseKey, setPrevCollapseKey] = useState(derivedStorageKey)
  const [collapsed, setCollapsed] = useState<boolean>(() => (collapseEnabled ? readCollapsed(derivedStorageKey) : false))
  if (prevCollapseKey !== derivedStorageKey) {
    setPrevCollapseKey(derivedStorageKey)
    if (collapseEnabled) {
      setCollapsed(readCollapsed(derivedStorageKey))
    }
  }
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      writeCollapsed(derivedStorageKey, next)
      return next
    })
  }, [derivedStorageKey])

  const [prevWidthKey, setPrevWidthKey] = useState(derivedWidthKey)
  const [widths, setWidths] = useState<ShellWidths>(() => readWidths(derivedWidthKey))
  if (prevWidthKey !== derivedWidthKey) {
    setPrevWidthKey(derivedWidthKey)
    setWidths(readWidths(derivedWidthKey))
  }

  const dragRef = useRef<{
    side: 'left' | 'right'
    startX: number
    startWidth: number
    shellEl: HTMLElement
    shellInner: number
    otherWidth: number
    gap: number
    railMin: number
    currentWidth: number
    hasMoved: boolean
    rafId: number | null
  } | null>(null)
  const [draggingSide, setDraggingSide] = useState<'left' | 'right' | null>(null)

  useEffect(() => {
    return () => {
      document.documentElement.classList.remove('railPageShell-isResizing')
    }
  }, [])

  const getDefaultWidth = (side: 'left' | 'right') => {
    if (columnSizes?.[side]) {
      const parsed = parseFloat(columnSizes[side]!)
      if (!Number.isNaN(parsed)) return parsed
    }
    if (side === 'left') {
      return variant === 'three-column' ? 240 : 380
    }
    return 300
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>, side: 'left' | 'right') => {
    if (e.button !== 0) return
    const shellEl = e.currentTarget.closest<HTMLElement>('.railPageShell')
    if (!shellEl) return

    try {
      e.currentTarget.setPointerCapture?.(e.pointerId)
    } catch {
      /* ignore */
    }

    const railMin = variant === 'three-column' ? RAIL_MIN_3C : RAIL_MIN_2C
    const colEl = shellEl.querySelector<HTMLElement>(
      side === 'left' ? '.railPageShell__rail' : '.railPageShell__sidecar'
    )
    const startWidth =
      colEl?.getBoundingClientRect().width ?? (widths[side] ?? getDefaultWidth(side))

    const style = window.getComputedStyle(shellEl)
    const paddingLeft = parseFloat(style.paddingLeft) || 0
    const paddingRight = parseFloat(style.paddingRight) || 0
    const clientW = shellEl.clientWidth || shellEl.getBoundingClientRect().width || 1200
    const shellInner = Math.max(0, clientW - paddingLeft - paddingRight)

    const otherEl =
      variant === 'three-column'
        ? shellEl.querySelector<HTMLElement>(
            side === 'left' ? '.railPageShell__sidecar' : '.railPageShell__rail'
          )
        : null
    const otherWidth =
      otherEl?.getBoundingClientRect().width ??
      (widths[side === 'left' ? 'right' : 'left'] ?? (side === 'left' ? SIDECAR_MIN : railMin))

    const gap = parseFloat(style.columnGap || style.gap) || 10

    dragRef.current = {
      side,
      startX: e.clientX,
      startWidth,
      shellEl,
      shellInner,
      otherWidth,
      gap,
      railMin,
      currentWidth: startWidth,
      hasMoved: false,
      rafId: null,
    }

    setDraggingSide(side)
    document.documentElement.classList.add('railPageShell-isResizing')
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return

    const dx = e.clientX - drag.startX
    if (Math.abs(dx) > 1) {
      drag.hasMoved = true
    }
    let raw = drag.side === 'left' ? drag.startWidth + dx : drag.startWidth - dx

    if (collapseEnabled && drag.side === 'left') {
      if (!collapsed && shouldSnapCollapse(raw, drag.railMin)) {
        document.documentElement.classList.remove('railPageShell-isResizing')
        try {
          e.currentTarget.releasePointerCapture?.(e.pointerId)
        } catch {
          /* ignore */
        }
        drag.shellEl.style.removeProperty('--shell-left-col')
        dragRef.current = null
        setDraggingSide(null)
        toggleCollapsed()
        return
      }

      if (collapsed && shouldSnapExpand(dx)) {
        toggleCollapsed()
        const savedWidth =
          widths.left ?? (columnSizes?.left ? parseFloat(columnSizes.left) : drag.railMin)
        drag.startWidth = savedWidth
        drag.startX = e.clientX
        raw = savedWidth
      }
    }

    const clamped = clampSide({
      side: drag.side,
      raw,
      shellInner: drag.shellInner,
      other: drag.otherWidth,
      gap: drag.gap,
      variant: variant === 'three-column' ? 'three-column' : 'two-column',
    })
    drag.currentWidth = clamped.width

    if (drag.rafId !== null) {
      cancelAnimationFrame(drag.rafId)
    }

    const currentDrag = drag
    drag.rafId = requestAnimationFrame(() => {
      if (!dragRef.current) return
      const prop = currentDrag.side === 'left' ? '--shell-left-col' : '--shell-right-col'
      currentDrag.shellEl.style.setProperty(prop, `${clamped.width}px`)
      if (currentDrag.side === 'right') {
        currentDrag.shellEl.style.setProperty('--shell-right-col-collapsed', `${clamped.width}px`)
      }
    })
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return

    if (drag.rafId !== null) {
      cancelAnimationFrame(drag.rafId)
    }

    document.documentElement.classList.remove('railPageShell-isResizing')
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    } catch {
      /* ignore */
    }

    const side = drag.side
    const finalWidth = drag.currentWidth
    const hasMoved = drag.hasMoved
    dragRef.current = null
    setDraggingSide(null)

    if (hasMoved) {
      setWidths((prev) => {
        const next = { ...prev, [side]: finalWidth }
        writeWidths(derivedWidthKey, next)
        return next
      })
    }
  }

  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>, side: 'left' | 'right') => {
    setWidths((prev) => {
      const next = { ...prev }
      delete next[side]
      writeWidths(derivedWidthKey, next)
      return next
    })

    const shellEl = e.currentTarget.closest<HTMLElement>('.railPageShell')
    if (shellEl) {
      if (side === 'left') {
        shellEl.style.removeProperty('--shell-left-col')
      } else {
        shellEl.style.removeProperty('--shell-right-col')
        shellEl.style.removeProperty('--shell-right-col-collapsed')
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, side: 'left' | 'right') => {
    const delta = keyboardDelta(e.key, e.shiftKey, side)
    if (delta === null) return

    e.preventDefault()

    if (side === 'left' && collapseEnabled && collapsed && (e.key === 'ArrowRight' || delta === 'max')) {
      toggleCollapsed()
      return
    }

    const shellEl = e.currentTarget.closest<HTMLElement>('.railPageShell')
    if (!shellEl) return

    const railMin = variant === 'three-column' ? RAIL_MIN_3C : RAIL_MIN_2C
    const colEl = shellEl.querySelector<HTMLElement>(
      side === 'left' ? '.railPageShell__rail' : '.railPageShell__sidecar'
    )
    const currentWidth =
      widths[side] ?? (colEl?.getBoundingClientRect().width ? Math.round(colEl.getBoundingClientRect().width) : getDefaultWidth(side))

    const style = window.getComputedStyle(shellEl)
    const paddingLeft = parseFloat(style.paddingLeft) || 0
    const paddingRight = parseFloat(style.paddingRight) || 0
    const clientW = shellEl.clientWidth || shellEl.getBoundingClientRect().width || 1200
    const shellInner = Math.max(0, clientW - paddingLeft - paddingRight)

    const otherEl =
      variant === 'three-column'
        ? shellEl.querySelector<HTMLElement>(
            side === 'left' ? '.railPageShell__sidecar' : '.railPageShell__rail'
          )
        : null
    const otherWidth =
      otherEl?.getBoundingClientRect().width ??
      (widths[side === 'left' ? 'right' : 'left'] ?? (side === 'left' ? SIDECAR_MIN : railMin))

    const gap = parseFloat(style.columnGap || style.gap) || 10

    let targetWidth: number
    if (delta === 'min') {
      targetWidth = side === 'left' ? railMin : SIDECAR_MIN
    } else if (delta === 'max') {
      targetWidth = shellInner
    } else {
      targetWidth = currentWidth + delta
    }

    const clamped = clampSide({
      side,
      raw: targetWidth,
      shellInner,
      other: otherWidth,
      gap,
      variant: variant === 'three-column' ? 'three-column' : 'two-column',
    })

    setWidths((prev) => {
      const next = { ...prev, [side]: clamped.width }
      writeWidths(derivedWidthKey, next)
      return next
    })
  }

  const hasSidecar = rightSidecar !== undefined && rightSidecar !== null
  const [internalSidecarOpen, setInternalSidecarOpen] = useState(false)
  const isSidecarControlled = sidecarOpenProp !== undefined
  const isSidecarOpen = isSidecarControlled ? sidecarOpenProp : internalSidecarOpen

  const handleSidecarOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isSidecarControlled) {
        setInternalSidecarOpen(nextOpen)
      }
      onSidecarOpenChange?.(nextOpen)
    },
    [isSidecarControlled, onSidecarOpenChange],
  )

  const previousFocusRef = useRef<HTMLElement | null>(null)
  const sidecarRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isPhoneShellActive) return

    if (isSidecarOpen) {
      previousFocusRef.current = (document.activeElement as HTMLElement) ?? null
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          handleSidecarOpenChange(false)
        }
      }
      window.addEventListener('keydown', handleKeyDown, true)

      const frame = requestAnimationFrame(() => {
        if (!sidecarRef.current) return
        const focusable = sidecarRef.current.querySelector<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )
        if (focusable) {
          focusable.focus()
        } else {
          sidecarRef.current.focus()
        }
      })

      return () => {
        window.removeEventListener('keydown', handleKeyDown, true)
        cancelAnimationFrame(frame)
      }
    } else if (previousFocusRef.current) {
      const elToFocus = previousFocusRef.current
      previousFocusRef.current = null
      const frame = requestAnimationFrame(() => {
        elToFocus.focus()
      })
      return () => cancelAnimationFrame(frame)
    }
  }, [isPhoneShellActive, isSidecarOpen, handleSidecarOpenChange])

  const collapseContextValue = useMemo<RailPageShellCollapseState>(
    () => ({ collapsed: collapseEnabled && collapsed, useInitials: useCollapsedInitials }),
    [collapseEnabled, collapsed, useCollapsedInitials],
  )

  const rootClass = joinClasses(
    'railPageShell',
    `railPageShell--${variant}`,
    chrome ? undefined : 'railPageShell--no-chrome',
    collapseEnabled && collapsed && 'railPageShell--collapsed',
    collapseEnabled && collapsed && collapsedRailBehavior === 'hide' && 'railPageShell--collapsed-hide',
    phoneLayout === 'page' ? 'railPageShell--phone-page' : isPhone ? 'railPageShell--phone-shell' : undefined,
    className,
  )

  const columnStyle = useMemo<React.CSSProperties | undefined>(() => {
    const props: Record<string, string> = {}
    if (columnSizes?.left) props['--shell-left-col'] = columnSizes.left
    if (columnSizes?.right) props['--shell-right-col'] = columnSizes.right

    if (widths.left !== undefined) {
      const railMin = variant === 'three-column' ? RAIL_MIN_3C : RAIL_MIN_2C
      props['--shell-left-col'] = `clamp(${railMin}px, ${widths.left}px, 45%)`
    }
    if (widths.right !== undefined) {
      props['--shell-right-col'] = `clamp(${SIDECAR_MIN}px, ${widths.right}px, 45%)`
      props['--shell-right-col-collapsed'] = `clamp(${SIDECAR_MIN}px, ${widths.right}px, 45%)`
    }

    return Object.keys(props).length > 0 ? (props as React.CSSProperties) : undefined
  }, [columnSizes, widths, variant])

  const hasSplitSlots = leftRail === undefined && (
    leftRailList !== undefined
    || leftRailFilter !== undefined
    || leftRailExtras !== undefined
    || leftRailBottom !== undefined
  )
  const hasIdentityAndBody = identity !== undefined && (
    leftRail !== undefined || hasSplitSlots
  )

  const railScroll = useEdgeFadeScroll<HTMLDivElement>()

  const isCollapsed = collapseEnabled && collapsed
  const showFilter = leftRailFilter !== undefined && !isCollapsed
  const bottomExpanded = leftRailBottom !== undefined && !isCollapsed
  const bottomCollapsed = isCollapsed && leftRailBottomIcon !== undefined
  const renderLeftRail = () => (
    <aside className="railPageShell__card railPageShell__rail">
      {!isPhoneShellActive && identity && (
        <IdentityBlock
          identity={identity}
          collapsed={isCollapsed}
          onToggleCollapsed={collapseEnabled ? toggleCollapsed : undefined}
        />
      )}
      {!isPhoneShellActive && hasIdentityAndBody && <hr className="railPageShell__rail__hr railPageShell__rail__hr--identity" />}
      {leftRail !== undefined && (
        <div
          ref={railScroll.ref}
          {...railScroll.scrollProps}
          className="railPageShell__rail__scroll edgeFadeScroll"
        >
          {leftRail}
        </div>
      )}
      {hasSplitSlots && (
        <div
          ref={railScroll.ref}
          {...railScroll.scrollProps}
          className="railPageShell__rail__scroll edgeFadeScroll"
        >
          {leftRailList !== undefined && (
            <div className="railPageShell__rail__section railPageShell__rail__section--list">
              {leftRailList}
            </div>
          )}
          {showFilter && (
            <div className="railPageShell__rail__section railPageShell__rail__section--filter">
              {leftRailFilter}
            </div>
          )}
          {leftRailExtras !== undefined && (
            <div className="railPageShell__rail__section railPageShell__rail__section--extras">
              {leftRailExtras}
            </div>
          )}
        </div>
      )}
      {!isPhoneShellActive && bottomExpanded && (
        <div className="railPageShell__rail__bottom">
          {leftRailBottom}
        </div>
      )}
      {!isPhoneShellActive && bottomCollapsed && (
        <div className="railPageShell__rail__bottom railPageShell__rail__bottom--collapsed" aria-hidden="true">
          {leftRailBottomIcon}
        </div>
      )}
    </aside>
  )

  const renderMiddleStage = () => (
    <main className="railPageShell__stage">
      {middleHeader && <RailPageShellSectionHeader {...middleHeader} />}
      {middleStage !== undefined && (
        <div className="railPageShell__stage__scroll">{middleStage}</div>
      )}
      {isPhoneShellActive && leftRailBottom !== undefined && phoneRailBottom !== 'hide' && (
        <div className="railPageShell__phoneRailBottom">{leftRailBottom}</div>
      )}
    </main>
  )

  const renderRightSidecar = () => {
    if (!hasSidecar) return null
    return (
      <aside
        ref={sidecarRef}
        tabIndex={-1}
        className={joinClasses(
          'railPageShell__sidecar',
          collapseEnabled && collapsed && 'railPageShell__sidecar--expanded',
          isSidecarOpen && 'railPageShell__sidecar--sheet-open',
        )}
      >
        {phoneLayout !== 'page' && isPhone && (
          <Pressable
            className="railPageShell__sidecarHandle"
            aria-label={getNlsString('NLSCommonRailPageShellSidecarSheet')}
            aria-expanded={isSidecarOpen}
            onClick={() => handleSidecarOpenChange(!isSidecarOpen)}
          >
            <span className="railPageShell__sidecarHandleBar" aria-hidden="true" />
          </Pressable>
        )}
        <div className="railPageShell__sidecar__scroll">{rightSidecar}</div>
      </aside>
    )
  }

  const renderRightStage = () => (
    <main className="railPageShell__rightStage">
      {rightStage}
      {isPhoneShellActive && leftRailBottom !== undefined && phoneRailBottom !== 'hide' && (
        <div className="railPageShell__phoneRailBottom">{leftRailBottom}</div>
      )}
    </main>
  )

  const renderOneColumn = () => (
    <>
      {header !== undefined && (
        <header className="railPageShell__header">{header}</header>
      )}
      {body !== undefined && (
        <section className="railPageShell__body">{body}</section>
      )}
    </>
  )

  const renderSash = (side: 'left' | 'right') => {
    const railMin = variant === 'three-column' ? RAIL_MIN_3C : RAIL_MIN_2C
    const min = side === 'left' ? railMin : SIDECAR_MIN
    const max = 1200
    const current = widths[side] ?? getDefaultWidth(side)
    const isDragging = draggingSide === side

    return (
      <div
        key={`sash-${side}`}
        role="separator"
        aria-orientation="vertical"
        aria-label={getNlsString(
          side === 'left'
            ? 'NLSCommonRailPageShellResizeRail'
            : 'NLSCommonRailPageShellResizeSidecar'
        )}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={Math.round(current)}
        tabIndex={0}
        className={joinClasses(
          'railPageShell__sash',
          `railPageShell__sash--${side}`,
          isDragging && 'is-dragging'
        )}
        onPointerDown={(e) => handlePointerDown(e, side)}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onLostPointerCapture={handlePointerUp}
        onDoubleClick={(e) => handleDoubleClick(e, side)}
        onKeyDown={(e) => handleKeyDown(e, side)}
      />
    )
  }

  return (
    <RailPageShellCollapseContext.Provider value={collapseContextValue}>
      <section className={rootClass} aria-label={ariaLabel} style={columnStyle}>
        {variant === 'three-column' && (
          <>
            {renderLeftRail()}
            {renderMiddleStage()}
            {isPhoneShellActive && hasSidecar && isSidecarOpen && (
              <Pressable
                className="railPageShell__scrim"
                aria-label={getNlsString('NLSCommonActionClose')}
                onClick={() => handleSidecarOpenChange(false)}
              />
            )}
            {renderRightSidecar()}
            {resizeEnabled && (
              <>
                {renderSash('left')}
                {renderSash('right')}
              </>
            )}
          </>
        )}

        {variant === 'two-column' && (
          <>
            {renderLeftRail()}
            {renderRightStage()}
            {resizeEnabled && renderSash('left')}
          </>
        )}

        {variant === 'one-column' && renderOneColumn()}

        {variant === 'full' && children}
      </section>
    </RailPageShellCollapseContext.Provider>
  )
}
